import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BookOpen, ArrowLeft, PlugZap } from "lucide-react";

export default function PolicyLibrary() {
  const [policies, setPolicies] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.policies().then(setPolicies).catch(() => {});
    api.integrations().then(setIntegrations).catch(() => {});
  }, []);

  const openDetail = (id) => api.policy(id).then(setDetail).catch(() => {});

  if (detail) {
    const active = detail.versions?.find((v) => v.active) || detail.versions?.[0];
    return (
      <div>
        <Button variant="ghost" size="sm" className="mb-3 text-slate-500" onClick={() => setDetail(null)} data-testid="policy-back"><ArrowLeft className="mr-1 h-4 w-4" /> Policy Library</Button>
        <PageHeader title={detail.name} subtitle={`${detail.policy_code} · ${detail.payer_name}`} />
        <InfoNote className="mb-4">Synthetic policy criterion for prototype validation only. This does not represent actual CMS coverage policy.</InfoNote>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="p-4">
            <div className="mb-3 text-sm font-semibold text-slate-800">Criteria & Rule Configuration ({active?.version})</div>
            {(active?.criteria || []).map((c) => (
              <div key={c.id} className="mb-2 rounded-md border border-slate-200 p-3 text-xs" data-testid={`crit-${c.criterion_code}`}>
                <div className="font-semibold text-slate-800">Criterion {c.criterion_code} — <span className="uppercase text-slate-500">{c.severity}</span></div>
                <div className="mt-0.5 text-slate-600">{c.description}</div>
                <div className="mt-1 font-mono text-[10px] text-slate-400">rule: {c.rule_type} · fact: {c.required_fact_type || "administrative"} · threshold ≥ {c.threshold}{c.contradiction_check ? " · contradiction-checked" : ""}</div>
              </div>
            ))}
          </Card>
          <div className="space-y-4">
            <Card className="p-4">
              <div className="mb-3 text-sm font-semibold text-slate-800">Source Metadata & Provenance</div>
              <dl className="space-y-1 text-xs">
                <Meta k="Originating organization" v={detail.originating_organization} />
                <Meta k="Source URI" v={detail.source_uri} />
                <Meta k="Source type" v={detail.source_type} />
                <Meta k="Jurisdiction" v={detail.jurisdiction} />
                <Meta k="Validation status" v={detail.validation_status} />
                <Meta k="Last reviewed" v={detail.last_reviewed} />
                <Meta k="Reviewer" v={detail.reviewer} />
              </dl>
            </Card>
            <Card className="p-4">
              <div className="mb-3 text-sm font-semibold text-slate-800">Version History</div>
              {detail.versions?.map((v) => (
                <div key={v.id} className="mb-1.5 flex items-center justify-between rounded border border-slate-200 p-2 text-xs">
                  <div><span className="font-semibold text-slate-800">v{v.version}</span> {v.active && <span className="ml-1 rounded bg-teal-100 px-1.5 text-[10px] text-teal-700">active</span>}
                    <div className="text-slate-500">Effective {v.effective_date} · {v.revision_notes}</div></div>
                  <span className="font-mono text-[10px] text-slate-400">{v.hash}</span>
                </div>
              ))}
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader testid="policy-library-header" title="Policy Library"
        subtitle="Synthetic demonstration policies and their provenance. Prototype 0.1 uses synthetic rules only." />
      <Card className="mb-4 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Policy ID</TableHead><TableHead>Policy</TableHead><TableHead>Payer</TableHead>
              <TableHead>Test</TableHead><TableHead>Version</TableHead><TableHead>Status</TableHead><TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {policies.map((p) => {
              const active = p.versions?.find((v) => v.active) || p.versions?.[0];
              return (
                <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" data-testid={`policy-row-${p.id}`} onClick={() => openDetail(p.id)}>
                  <TableCell className="font-mono text-xs"><BookOpen className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{p.policy_code}</TableCell>
                  <TableCell className="text-xs font-medium">{p.name}</TableCell>
                  <TableCell className="text-xs">{p.payer_name}</TableCell>
                  <TableCell className="text-xs">Respiratory Pathogen Panel</TableCell>
                  <TableCell className="text-xs">{active?.version}</TableCell>
                  <TableCell className="text-xs"><span className="rounded bg-teal-50 px-1.5 py-0.5 text-teal-700">{p.status}</span></TableCell>
                  <TableCell className="text-[10px] text-slate-500">{p.source_type}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><PlugZap className="h-4 w-4" /> Policy Source Integrations</div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {integrations.filter((i) => i.category === "Policy Source").concat(integrations.filter((i) => i.category !== "Policy Source")).slice(0, 3).map((i) => (
            <div key={i.id} className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3">
              <div className="text-sm font-medium text-slate-700">{i.name}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{i.status}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const Meta = ({ k, v }) => (<div className="flex justify-between gap-4"><dt className="text-slate-400">{k}</dt><dd className="max-w-[60%] truncate text-right font-medium text-slate-700">{v}</dd></div>);
